// One war on the client (docs/ARCHITECTURE.md §8): owns its room channel, the mirror scene and everything derived
// from the room's views, private state and events. HUD, VFX and screens read it through MatchView and act only
// through its command methods. Disposable: dispose() releases the room (terminating the local Worker), the realm's
// Core with the mirror scene and its systems, and every listener.

import { RpcError } from '@esengine/rpc'
import {
  InvariantViolation,
  Owner,
  RoomErrorCode,
  StrikeFlight,
  type CityIndex,
  type Command,
  type MatchSetupView,
  type PlayerId,
  type PlayerPrivateState,
  type RoomPresence,
  type SimEvent,
  type SimId,
  type SkirmishSetup,
  type StrikeTypeId,
  type ViewFrame,
} from '@taxwar/net'
import { Listeners, createLocalRoom, openOnlineRoom, type LinkState, type MatchChannel, type TransportError, type Unsubscribe } from '../net'
import { CityTracker } from './cityTracker'
import type { AttackView, CityLiveState, LatLonOut, MatchResult, MatchView, StrikeFlightOut, WarReport } from './contracts'
import { MirrorWorld } from './mirror'
import { InterpolationSystem, PoseTrack, RenderClock, type PresentationSystem } from './presentation'
import { AttackDetectionSystem } from './attacks'
import { RecapBuffer } from './recap'

/** How a war ended: decided by the sim, or the room connection was lost ("connection lost", §13). */
export type MatchEnd = { readonly kind: 'decided'; readonly result: MatchResult } | { readonly kind: 'lost'; readonly error: TransportError }

type JoinState = { readonly kind: 'joining' } | { readonly kind: 'joined'; readonly setup: MatchSetupView; readonly cities: CityTracker }
type RunState = { readonly kind: 'running' } | MatchEnd

/**
 * Creates the local room, subscribes to it, then starts the war. The Match is returned once joined. When the room
 * is lost while starting, the start fails with the loss (its reason), not with the call's generic rejection.
 */
export async function startMatch(setup: SkirmishSetup): Promise<Match> {
  const connection = await createLocalRoom()
  return joined(connection, async () => (await connection.startSkirmish(setup)))
}

/**
 * Connects to an online war room (the matchmaker's room URL, carrying this seat's claim), subscribes, then joins. The
 * war may not have started yet: the room's presence says who it is still waiting for (docs/ARCHITECTURE.md §15).
 */
export async function startOnlineMatch(roomUrl: string): Promise<Match> {
  const connection = await openOnlineRoom(roomUrl)
  return joined(connection, async () => (await connection.join()).setup)
}

/** The Match is created (subscribed) before the room is asked for the war, then joined with its answer. */
async function joined(connection: MatchChannel, requestSetup: () => Promise<MatchSetupView>): Promise<Match> {
  let match: Match
  try {
    match = new Match(connection)
  } catch (error) {
    connection.dispose()
    throw error
  }
  try {
    match.join(await requestSetup())
  } catch (error) {
    const end = match.end()
    match.dispose()
    throw end !== null && end.kind === 'lost' ? end.error : error
  }
  return match
}

const NO_SUBSCRIPTION: Unsubscribe = () => undefined

export class Match implements MatchView {
  private readonly mirror: MirrorWorld
  private readonly attacks: AttackDetectionSystem
  private readonly clock = new RenderClock()
  private readonly recap = new RecapBuffer()
  /** Aborted by dispose(): detaches every listener the Match registered on others' signals. */
  private readonly lifetime = new AbortController()
  private readonly subscriptions: readonly Unsubscribe[]
  private readonly eventListeners = new Listeners<readonly SimEvent[]>()
  private readonly privateListeners = new Listeners<PlayerPrivateState>()
  private readonly cityListeners = new Listeners<CityIndex>()
  private readonly resultListeners = new Listeners<MatchResult>()
  private readonly endListeners = new Listeners<MatchEnd>()
  private readonly attackListeners = new Listeners<readonly AttackView[]>()

  private joinState: JoinState = { kind: 'joining' }
  private runState: RunState = { kind: 'running' }
  private latestPrivate: PlayerPrivateState | null = null
  private fault: { readonly error: unknown } | null = null
  /** The room rejoined this seat: the next view frame is a full one, applied to an emptied mirror. */
  private resyncPending = false
  private pausedNow = false
  private disposed = false

  /** Subscribes to every room channel at once: the first view frame carries the spawns later deltas build on. */
  constructor(private readonly connection: MatchChannel) {
    this.mirror = MirrorWorld.create()
    this.attacks = new AttackDetectionSystem((simId) => this.mirror.find(simId), this.attackListeners.emit)
    try {
      this.mirror.addSystem(new InterpolationSystem(this.clock))
      this.mirror.addSystem(this.attacks)
    } catch (error) {
      this.mirror.dispose()
      throw error
    }
    const link = connection.link
    this.subscriptions = [
      connection.onView(this.handleView),
      connection.onPrivate(this.handlePrivate),
      connection.onEvents(this.handleEvents),
      connection.onLost(this.handleLost),
      link === null ? NO_SUBSCRIPTION : link.onResync(this.handleResync),
    ]
  }

  get setup(): MatchSetupView {
    return this.requireJoined().setup
  }

  get you(): PlayerId {
    return this.requireJoined().setup.you
  }

  get paused(): boolean {
    return this.pausedNow
  }

  /** The room accepted the war: city tracking starts from the setup map. */
  join(setup: MatchSetupView): void {
    if (this.joinState.kind !== 'joining') throw new InvariantViolation('match joined twice')
    const cities = new CityTracker(setup)
    this.joinState = { kind: 'joined', setup, cities }
    cities.refresh(this.mirror, this.emitCityChanged)
  }

  privateState(): PlayerPrivateState | null {
    return this.latestPrivate
  }

  cityState(index: CityIndex): CityLiveState {
    return this.requireJoined().cities.state(index)
  }

  visibleStrikes(out: SimId[]): number {
    const strikes = this.mirror.strikes
    out.length = 0
    for (let i = 0; i < strikes.length; i++) out.push(strikes[i] as SimId)
    return out.length
  }

  strikeFlight(simId: SimId, out: StrikeFlightOut): boolean {
    const entity = this.mirror.find(simId)
    if (entity === undefined) return false
    const flight = entity.getComponent(StrikeFlight)
    const owner = entity.getComponent(Owner)
    if (flight === null || owner === null) return false
    out.owner = owner.player as StrikeFlightOut['owner']
    out.strikeType = flight.typeId as StrikeTypeId
    out.originLat = flight.originLat
    out.originLon = flight.originLon
    out.targetLat = flight.targetLat
    out.targetLon = flight.targetLon
    out.flying = flight.onTarget === 0
    return true
  }

  entityPosition(simId: SimId, out: LatLonOut): boolean {
    const entity = this.mirror.find(simId)
    if (entity === undefined) return false
    const track = entity.getComponent(PoseTrack)
    if (track === null) return false
    out.lat = track.lat
    out.lon = track.lon
    return true
  }

  result(): MatchResult | null {
    return this.runState.kind === 'decided' ? this.runState.result : null
  }

  /** How the war ended, or null while it runs. */
  end(): MatchEnd | null {
    return this.runState.kind === 'running' ? null : this.runState
  }

  onEvents(listener: (events: readonly SimEvent[]) => void): Unsubscribe {
    return this.eventListeners.add(listener)
  }

  onAttacks(listener: (attacks: readonly AttackView[]) => void): Unsubscribe {
    return this.attackListeners.add(listener)
  }

  onPrivate(listener: (state: PlayerPrivateState) => void): Unsubscribe {
    return this.privateListeners.add(listener)
  }

  onCityChanged(listener: (index: CityIndex) => void): Unsubscribe {
    return this.cityListeners.add(listener)
  }

  /** Subscribing after the result delivers it at once. */
  onResult(listener: (result: MatchResult) => void): Unsubscribe {
    if (this.runState.kind === 'decided') listener(this.runState.result)
    return this.resultListeners.add(listener)
  }

  /** Decided or lost; subscribing after the end delivers it at once, so an end can never go unseen. */
  onEnd(listener: (end: MatchEnd) => void): Unsubscribe {
    if (this.runState.kind !== 'running') listener(this.runState)
    return this.endListeners.add(listener)
  }

  sendCommand(command: Command): number {
    return this.connection.sendCommand(command)
  }

  /** Single-player wars pause (D10); online wars never do. */
  get canPause(): boolean {
    return this.connection.pause !== null
  }

  async pause(): Promise<void> {
    if (await this.settleUnlessEnded(this.pauseControl().pause())) this.pausedNow = true
  }

  async resume(): Promise<void> {
    if (await this.settleUnlessEnded(this.pauseControl().resume())) this.pausedNow = false
  }

  /** Online rooms: who is connected and the room's countdowns. A local war never reports presence. */
  onPresence(listener: (presence: RoomPresence) => void): Unsubscribe {
    const feed = this.connection.presence
    return feed === null ? NO_SUBSCRIPTION : feed.onPresence(listener)
  }

  /** Online rooms: the link to the room, live or reconnecting after a drop. A local war never reports it. */
  onLink(listener: (state: LinkState) => void): Unsubscribe {
    const feed = this.connection.link
    return feed === null ? NO_SUBSCRIPTION : feed.onLink(listener)
  }

  private pauseControl(): NonNullable<MatchChannel['pause']> {
    const control = this.connection.pause
    if (control === null) throw new InvariantViolation('this war cannot be paused (online wars never pause, D10)')
    return control
  }

  /** The war report for the results screen; only once the war is decided. */
  report(): WarReport {
    const state = this.runState
    if (state.kind !== 'decided') throw new InvariantViolation('war report requested before the war was decided')
    const finalPrivate = this.latestPrivate
    if (finalPrivate === null) throw new InvariantViolation('war decided before any private state arrived')
    return { setup: this.setup, result: state.result, finalPrivate, recap: this.recap.snapshot() }
  }

  /** Once per animation frame, before anything reads positions: advances render time and runs the mirror systems. */
  frame(dtSeconds: number): void {
    if (this.fault !== null) throw this.fault.error
    this.clock.advance(dtSeconds)
    this.mirror.update(dtSeconds)
  }

  /** Runs `system` on the mirror scene each frame until `signal` aborts (or the Match is disposed). */
  addPresentationSystem(system: PresentationSystem, signal: AbortSignal): void {
    if (signal.aborted) throw new InvariantViolation('presentation system added with an already-aborted signal')
    this.mirror.addSystem(system)
    signal.addEventListener('abort', () => this.mirror.removeSystem(system), { once: true, signal: this.lifetime.signal })
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.lifetime.abort()
    for (const unsubscribe of this.subscriptions) unsubscribe()
    this.connection.dispose()
    this.mirror.dispose()
    this.eventListeners.clear()
    this.privateListeners.clear()
    this.cityListeners.clear()
    this.resultListeners.clear()
    this.endListeners.clear()
    this.attackListeners.clear()
  }

  /**
   * A pause/resume can cross the war's end in flight: the room answers matchEnded and the outcome is already on its
   * way, so there is nothing to change (false). Every other failure rejects.
   */
  private async settleUnlessEnded(request: Promise<void>): Promise<boolean> {
    try {
      await request
      return true
    } catch (error) {
      if (error instanceof RpcError && error.code === RoomErrorCode.matchEnded) return false
      throw error
    }
  }

  private requireJoined(): Extract<JoinState, { kind: 'joined' }> {
    const state = this.joinState
    if (state.kind !== 'joined') throw new InvariantViolation('match used before the room accepted the war')
    return state
  }

  private readonly emitCityChanged = (index: CityIndex): void => {
    this.cityListeners.emit(index)
  }

  /** A failed frame leaves the mirror inconsistent: the fault is latched, rethrown here and by every frame(). */
  private latch(error: unknown): never {
    this.fault = { error }
    throw error
  }

  private readonly handleResync = (): void => {
    this.resyncPending = true
  }

  private readonly handleView = (frame: ViewFrame): void => {
    if (this.fault !== null) return
    try {
      // The stale world stays on screen until the full frame replaces it in one go.
      if (this.resyncPending) {
        this.resyncPending = false
        this.mirror.reset()
        this.attacks.forgetBaselines()
      }
      this.mirror.applyFrame(frame)
      this.clock.onFrame(frame.tick)
      const join = this.joinState
      if (join.kind === 'joined') join.cities.refresh(this.mirror, this.emitCityChanged)
    } catch (error) {
      this.latch(error)
    }
  }

  private readonly handlePrivate = (state: PlayerPrivateState): void => {
    if (this.fault !== null) return
    this.latestPrivate = state
    this.privateListeners.emit(state)
  }

  private readonly handleEvents = (events: readonly SimEvent[]): void => {
    if (this.fault !== null) return
    this.recap.record(events)
    this.eventListeners.emit(events)
    for (let i = 0; i < events.length; i++) {
      const event = events[i] as SimEvent
      if (event.kind === 'outcomeDecided') this.finish({ kind: 'decided', result: { outcome: event.outcome, winner: event.winner, tick: event.tick } })
    }
  }

  private readonly handleLost = (error: TransportError): void => {
    // A decided war stays decided: the room may go away once it has nothing left to send.
    if (this.runState.kind !== 'running') return
    this.finish({ kind: 'lost', error })
  }

  private finish(end: MatchEnd): void {
    if (this.runState.kind !== 'running') throw new InvariantViolation(`war ended twice (${this.runState.kind}, then ${end.kind})`)
    this.runState = end
    if (end.kind === 'decided') this.resultListeners.emit(end.result)
    this.endListeners.emit(end)
  }
}
